import { useCallback, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useMutation, useQuery } from '@connectrpc/connect-query'

import { listContracts } from '@enechain/eposition-proto/bffcommon/v1/eex_service-EEXService_connectquery'
import { simulateOrders } from '@enechain/eposition-proto/bffportal/v1/simulation_service-SimulationService_connectquery'
import { SimulateOrdersRequestSchema } from '@enechain/eposition-proto/bffportal/v1/simulation_service_pb'
import {
  OrderSimulationRelationWithNumberSchema,
  OrderWithNumber,
  OrderWithNumberSchema,
} from '@enechain/eposition-proto/devtool/v1/service_pb'
import { OrderSchema } from '@enechain/eposition-proto/external/v1/model_pb'
import { ECLEAR_OPTION_CATEGORY } from '@enechain/eposition-ui/src/models/eclearOption'
import { getErrorMessage } from '@enechain/eposition-utils'

import { convertToAvailableDealSimulationRelationModels } from '~/pages/Simulation/models/AvailableDealSimulationRelationModel'
import {
  MOCK_PORTFOLIO_ID,
  convertLegsFromOrderPatternModel,
  getSpreadType,
} from '~/pages/Simulation/models/SimulationOrderPattern'
import {
  FeeResultModel,
  convertFeeResultModels,
} from '~/pages/Simulation/models/SimulationResultModel/FeeResultModel'
import { convertAvailableDealSimulationForOption1Models } from '~/pages/Simulation/models/SimulationResultModel/ResultAvailableDealSimulationDetailForOption1Models'
import { convertAvailableDealSimulationForOption2Models } from '~/pages/Simulation/models/SimulationResultModel/ResultAvailableDealSimulationDetailForOption2Models'
import { ResultAvailableDealSimulationDetailResult } from '~/pages/Simulation/models/SimulationResultModel/ResultAvailableDealSimulationDetailResult'
import {
  ResultSimulationForOption1Model,
  convertResultSimulationForOption1Model,
} from '~/pages/Simulation/models/SimulationResultModel/ResultSimulationForOption1Model'
import {
  ResultSimulationForOption2Model,
  convertResultSimulationForOption2Model,
} from '~/pages/Simulation/models/SimulationResultModel/ResultSimulationForOption2Model'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'
import { useGetMe } from '~/pages/hooks/useGetMe'

type Result = {
  invokeAvailableDealSimulation: (props: {
    value: OrderSimulationSchema
    onSuccess: () => void
    onError: (error: string) => void
  }) => Promise<void>
  progress: number
  resultDetail: ResultAvailableDealSimulationDetailResult
  feeResults: FeeResultModel[] | undefined
  isProcessing: boolean
}

export function useAvailableDealSimulation(): Result {
  const [progress, setProgress] = useState(0)
  const [resultDetail, setResultDetail] =
    useState<ResultAvailableDealSimulationDetailResult>(undefined)
  const [feeResults, setFeeResults] = useState<FeeResultModel[] | undefined>(
    undefined,
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const { optionCategory } = useGetMe()

  const { mutateAsync: simulationMutate } = useMutation(simulateOrders)
  const { data: eexContractData } = useQuery(
    listContracts,
    {},
    { throwOnError: false },
  )
  const invokeAvailableDealSimulation = useCallback(
    async ({
      value,
      onSuccess,
      onError,
    }: {
      value: OrderSimulationSchema
      onSuccess: () => void
      onError: (error: string) => void
    }) => {
      setIsProcessing(true)
      setProgress(0)
      setResultDetail(undefined)
      setFeeResults(undefined)

      const eexContracts = eexContractData?.contracts ?? []

      const { models: relationModel, error: convertError } =
        convertToAvailableDealSimulationRelationModels(value, eexContracts)

      if (convertError !== undefined) {
        onError(convertError)
        return
      }

      if (relationModel === undefined) {
        setIsProcessing(false)
        return
      }

      const orders: OrderWithNumber[] = relationModel.orderPatterns.map(
        (order) => {
          const legs = convertLegsFromOrderPatternModel(order)
          return create(OrderWithNumberSchema, {
            orderNumber: order.orderNumber,
            order: create(OrderSchema, {
              legs,
              platformId: MOCK_PORTFOLIO_ID,
              productSpreadType: getSpreadType(order),
            }),
          })
        },
      )

      const requestParamsArray = relationModel.simulationRelations.map(
        (relation, index) =>
          create(SimulateOrdersRequestSchema, {
            orders,
            simulationRelations: [
              create(OrderSimulationRelationWithNumberSchema, {
                relationNumber: index + 1,
                orderNumbers: relation.orderNumbers,
              }),
            ],
            caseId: '',
            withUnitPriceBuffer: true,
          }),
      )

      const detailsForOption1: ResultSimulationForOption1Model[] = []
      const detailsForOption2: ResultSimulationForOption2Model[] = []

      try {
        for (const [index, requestParams] of requestParamsArray.entries()) {
          const response = await simulationMutate(requestParams)

          if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_1) {
            detailsForOption1.push(
              ...convertResultSimulationForOption1Model(response),
            )
          } else if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_2) {
            detailsForOption2.push(
              ...convertResultSimulationForOption2Model(response),
            )
          }

          // TODO: どうやってfeeのリストを取得するかは再検討
          // AvailableDealSimulationRelationModelで全部の注文含むrelation番号返す関数つくるとか
          if (index === requestParamsArray.length - 1) {
            setFeeResults(
              convertFeeResultModels(response, relationModel.orderPatterns),
            )
          }
          setProgress(((index + 1) / requestParamsArray.length) * 100)
        }

        if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_1) {
          setResultDetail(
            convertAvailableDealSimulationForOption1Models(detailsForOption1),
          )
        } else if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_2) {
          setResultDetail(
            convertAvailableDealSimulationForOption2Models(detailsForOption2),
          )
        }
        onSuccess()
      } catch (error) {
        setResultDetail(undefined)
        setFeeResults(undefined)
        const errorMessage = getErrorMessage({ error })
        onError(errorMessage)
      }
      setIsProcessing(false)
    },
    [optionCategory, simulationMutate, eexContractData?.contracts],
  )

  return {
    invokeAvailableDealSimulation,
    progress,
    resultDetail,
    feeResults,
    isProcessing,
  }
}
